import { Card } from "@/components/ui/card";
import { formatINR } from "@/lib/api";

const BAND_COLORS = {
  A: "bg-emerald-100 text-emerald-800 border-emerald-300",
  B: "bg-teal-100 text-teal-800 border-teal-300",
  C: "bg-amber-100 text-amber-800 border-amber-300",
  D: "bg-orange-100 text-orange-800 border-orange-300",
  E: "bg-red-100 text-red-800 border-red-300",
};

function Stat({ label, value, accent }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{label}</div>
      <div className={`text-sm font-semibold font-mono truncate ${accent || "text-slate-900"}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function CreditDecisionSummary({ app }) {
  const b = app.borrower || {};
  const cd = app.credit_decision || {};
  const val = app.valuation || {};
  const cf = app.cashflow_analysis || {};
  const risk = app.risk_score || {};
  const cardr = app.cardr || {};
  const loan = app.loan || {};
  const uw = app.underwriter_decision || {};

  const pct = (x) => (x || x === 0 ? `${Math.round(x * 1000) / 10}%` : "—");

  return (
    <Card className="p-4 border-slate-200 mb-4 bg-slate-50/60" data-testid="credit-decision-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Credit Decision Summary</div>
        {risk.band && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${BAND_COLORS[risk.band] || ""}`} data-testid="summary-risk-band">
            CarFin {risk.carfin_score} · Band {risk.band} ({risk.band_label})
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-4 gap-y-3">
        <Stat label="Requested" value={formatINR(loan.desired_loan_amount)} />
        <Stat label="System eligible" value={formatINR(cd.approved_amount)} accent="text-teal-700" />
        <Stat label="Lendable value" value={formatINR(val.lendable_value)} />
        <Stat label="LTV" value={pct(cd.ltv)} />
        <Stat label="CarDr grade" value={cardr.health_grade || val.health_grade} />
        <Stat label="CIBIL" value={b.cibil_score || "No hit"} />
        <Stat label="FOIR" value={pct(cd.foir || cf.foir_projected)} />
        <Stat label="Declared income" value={formatINR(b.monthly_income)} />
        <Stat label="Verified income" value={cf.verified_monthly_income ? formatINR(cf.verified_monthly_income) : "—"} />
        <Stat label="Existing EMI" value={formatINR(b.existing_emi)} />
        <Stat label="Avg balance" value={cf.avg_monthly_balance ? formatINR(cf.avg_monthly_balance) : "—"} />
        <Stat label="Rec. ROI" value={risk.recommended_roi ? `${risk.recommended_roi}%` : cd.roi ? `${cd.roi}%` : "—"} />
        <Stat label="Rec. tenure" value={risk.recommended_tenure_months ? `${risk.recommended_tenure_months} mo` : cd.tenure_months ? `${cd.tenure_months} mo` : "—"} />
        <Stat label="Engine decision" value={cd.decision} accent={cd.decision === "approve" ? "text-emerald-700" : cd.decision === "reject" ? "text-red-700" : "text-amber-700"} />
        <Stat label="UW decision" value={uw.decision || "Pending"} />
        <Stat label="Stage" value={app.status} />
      </div>
      {(risk.stipulations || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="summary-stipulations">
          {risk.stipulations.map((s, i) => (
            <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-300 text-slate-700">{s}</span>
          ))}
        </div>
      )}
    </Card>
  );
}
